import React, { useContext, useReducer, useCallback } from 'react';
import { IOcrMapsContext } from '../types';
import { OcrMap } from '@cmr/ocr-common';
import reducer, { initState } from '../reducers';
import actions from '../actions';
import { useApi } from '@cmr/react-api-hook';
import { compare } from 'fast-json-patch';

const mapsEndpoint = `${process.env['REACT_APP_OCR_API_ENDPOINT']}maps`;
export const OcrMapsContext = React.createContext<IOcrMapsContext | null>(null);
export const useOcrMaps = () => useContext(OcrMapsContext)!;

interface OcrMapsProviderOptions {
  children: React.ReactElement;
}

export const OcrMapsProvider = ({ children }: OcrMapsProviderOptions) => {
  const [state, dispatch] = useReducer(reducer, initState);
  const { apiFetch } = useApi();

  const fetch = useCallback(
    () => apiFetch(mapsEndpoint, dispatch, actions.fetch),
    [apiFetch],
  );

  const create = (map: OcrMap) =>
    apiFetch(
      mapsEndpoint,
      dispatch,
      actions.create,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(map),
      },
      map,
    );

  const patchMap = useCallback(
    (updated: OcrMap) => {
      const orig = state.maps.find((m) => m.id === updated.id);

      if (!orig) return Promise.resolve();

      const patch = compare(orig, updated);

      return apiFetch(`${mapsEndpoint}/${orig.id}`, dispatch, actions.patch, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(patch),
      });
    },
    [apiFetch, state.maps],
  );

  return (
    <OcrMapsContext.Provider
      value={{
        ...state,
        create,
        fetch,
        patch: patchMap,
      }}
    >
      {children}
    </OcrMapsContext.Provider>
  );
};
